// The reader chat's notes on a trace's lanes.
//
// The chat in the reading dock pins a note at each turn its answer points at
// (`trace_annotate`), on one lane's mark or on the turn, and the thread holds
// them for the session (traceChatThreads.js). This module places them on the
// axis the view has loaded and shapes them the way the comment pins, the Notes
// strip and a note's card read them, so they draw through the same pieces the
// chat's knowledge-graph claims do.
//
// A note carries the turn number the axis PRINTS (`printed`): a conversation
// that loads its newest page first shifts every loaded index when an earlier
// page lands, and a note must keep pointing at the turn it was pinned on.

const clip = (text, n) => (text.length > n ? `${text.slice(0, n - 3)}...` : text);

/**
 * The notes on the loaded axis, each with its loaded `turn` and the `cell` it
 * sits on (null for a note on the turn, or a lane the view does not chart at
 * that turn any more). Notes on turns not loaded are left out.
 */
export function placeNotes(notes, { turnBase = 0, turnCount = 0, hasMark = () => false } = {}) {
    const out = [];
    for (const n of notes || []) {
        const turn = Number(n?.printed) - (Number(turnBase) || 0);
        if (!Number.isInteger(turn) || turn < 0 || turn >= turnCount) continue;
        const cell = n.lane && hasMark(n.lane, turn) ? { lane: n.lane, turn } : null;
        out.push({ ...n, turn, cell });
    }
    return out;
}

// Pins, one per place: the notes on one lane's mark share its pin, and the
// notes on one turn share a pin over the Notes strip. `claims` names the
// notes a pin holds, the field the comment-pin pieces read.
export function noteAnchors(placed) {
    const byKey = new Map();
    for (const n of placed || []) {
        const key = n.cell ? `note cell ${n.cell.lane} ${n.cell.turn}` : `note turn ${n.turn}`;
        if (!byKey.has(key)) byKey.set(key, { id: key, lane: n.cell?.lane ?? null, turn: n.turn, claims: [] });
        byKey.get(key).claims.push(n.id);
    }
    return [...byKey.values()];
}

// The Notes strip: a mark on each turn a note on a lane's mark rests on (the
// turns whose notes stand on the strip already have their pin there).
export function noteStripMarks(placed) {
    const byTurn = new Map();
    for (const n of placed || []) {
        if (!n.cell) continue;
        if (!byTurn.has(n.turn)) byTurn.set(n.turn, []);
        byTurn.get(n.turn).push(n);
    }
    return [...byTurn.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([turn, ns]) => ({ turn, weight: "clear", label: clip(ns[0].text, 90) }));
}

export function noteStripSummary(placed, held = placed) {
    const n = (placed || []).length;
    const off = (held || []).length - n;
    if (!n) return off > 0 ? `${off} note${off === 1 ? "" : "s"} on turns not loaded` : "no notes";
    return `${n} note${n === 1 ? "" : "s"} from the reader${off > 0 ? `, ${off} on turns not loaded` : ""}`;
}

/**
 * A follow-up typed on a note's card, as the reader chat is sent it: the
 * reader's words, then which of its notes they were looking at.
 */
export function noteReplyText({ text, notes = [], turnLabel = null } = {}) {
    const words = String(text || "").trim();
    const where = turnLabel != null ? ` at T${turnLabel}` : "";
    const about = notes
        .map((n) => `${n.laneLabel ? `${n.laneLabel}, ` : ""}"${clip(String(n.text || ""), 120)}"`)
        .join("; ");
    return about ? `${words}\n\n(Asked from your note${notes.length === 1 ? "" : "s"} on the lanes${where}: ${about}.)` : words;
}
